import { useEffect, useState } from 'react'
import { useHits, useInstantSearch } from 'react-instantsearch'
import { Hit, BaseHit } from 'instantsearch.js/es/types/results'
import queryString from 'query-string'

import Loading from '@atoms/Loading'
import ArticleList from '@src/components/ArticleList'
import { Article, useArticle } from '@models/Article'
import { useTranslation } from '@models/Translation'

import * as style from './style.module.sass'

export type ArticleWithHit = Article & {
  hit: Hit<BaseHit>
}

const Hits = () => {
  const { locale } = useTranslation()
  const { hits, results, sendEvent } = useHits()
  const [articlesWithHit, setArticlesWithHit] = useState<ArticleWithHit[]>([])
  // Convert to Article type first to add hit and make it ArticleWithHit
  const { articleMap } = useArticle()
  const [gtmClickType, setGtmClickType] = useState('search-query')

  const { status } = useInstantSearch()
  const loading =
    status === 'loading' || status === 'stalled' || results === undefined

  useEffect(() => {
    if (loading) {
      setArticlesWithHit([])
      return
    }

    const search = typeof window !== 'undefined' ? window.location.search : ''
    const { ref } = queryString.parse(search || '')
    setGtmClickType(`search-${ref ?? 'query'}`)

    setArticlesWithHit(
      hits
        .map(hit => {
          return {
            ...articleMap[String(hit.blogId)],
            hit,
          }
        })
        .filter(
          articleWithHit => articleWithHit.blogId !== undefined,
        ) as ArticleWithHit[],
    )
  }, [articleMap, hits, status])

  const SearchText = () => {
    if (loading) {
      return (
        <div className={style.loadingContainer}>
          <Loading />
        </div>
      )
    }

    const searchText =
      locale === 'ja'
        ? `「${results.query}」の検索結果 ー ${articlesWithHit.length}件`
        : `${results.query}: ${articlesWithHit.length} results`

    return <span className={style.query}>{searchText}</span>
  }

  return (
    <>
      <div className={style.text}>
        <SearchText />
        {!loading && articlesWithHit.length === 0 && (
          <div className={style.noResult}>
            {locale === 'ja' ? (
              <>
                「{results?.query}」<br data-only="sp" />
                に関する記事はみつかりませんでした
              </>
            ) : (
              <>No results for {results?.query}</>
            )}
          </div>
        )}
      </div>
      <div className={style.list}>
        <ArticleList
          articles={articlesWithHit}
          gtmClickType={gtmClickType}
          gtmLabel={results?.query}
          handleArticleClick={article => {
            sendEvent(
              'click',
              (article as ArticleWithHit).hit,
              'Article Clicked',
            )
          }}
          displaysCategory
        />
      </div>
    </>
  )
}

export default Hits
